import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useParams, useNavigate } from 'react-router-dom';
import useDrawingsApi from '../hooks/useDrawingsApi';
import { toast } from 'react-toastify';
// PDF.js worker setup using bundled entry
// Use PDF.js worker from CDN for consistent loading
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function InlineEditorPage() {
  const { id: bidId } = useParams();
  const navigate = useNavigate();
  const { listTree, downloadPage, updatePageMeta } = useDrawingsApi();
  const [tree, setTree] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewPageId, setPreviewPageId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Thumbnail URLs for each page
  const [urls, setUrls] = useState({});
  // PDF viewer scale & ref
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  // Flattened pages list
  const pagesFlat = useMemo(() => Object.values(tree).flat(), [tree]);

  // Fetch presigned URLs for thumbnails
  useEffect(() => {
    pagesFlat.forEach(page => {
      if (!urls[page.id]) {
        downloadPage(bidId, page.id)
          .then(url => setUrls(prev => ({ ...prev, [page.id]: url })))
          .catch(err => console.error('Failed to fetch page thumbnail', err));
      }
    });
  }, [pagesFlat, urls, downloadPage, bidId]);

  // Fetch and initialize tree and first preview
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const data = await listTree(bidId);
        setTree(data);
        const first = Object.values(data).flat()[0];
        if (first) setPreviewPageId(first.id);
      } catch (err) {
        console.error(err);
        setError('Failed to load drawings');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [bidId, listTree]);

  useEffect(() => {
    if (!previewPageId) return;
    (async () => {
      try {
        const url = await downloadPage(bidId, previewPageId);
        setPreviewUrl(url);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [bidId, previewPageId, downloadPage]);

  const handleSaveField = async (pageId, field, value) => {
    try {
      await updatePageMeta(bidId, pageId, { [field]: value });
      toast.success('Saved');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  const onDocumentLoad = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };
  const onPageLoad = pageProxy => {
    const viewport = pageProxy.getViewport({ scale: 1 });
    const container = containerRef.current;
    if (container) {
      const { clientWidth, clientHeight } = container;
      const fitScale = Math.min(
        clientWidth / viewport.width,
        clientHeight / viewport.height
      );
      setScale(fitScale);
    }
  };


  return (
    <div className="flex h-screen overflow-hidden">
      {/* PDF Preview Pane */}
      <aside className="w-1/2 border-r flex flex-col">
        <div className="flex justify-between items-center p-2 border-b">
          <button onClick={() => navigate(`/bids/${bidId}/documents`)} className="text-gray-600 hover:text-gray-800">
            Close Editor
          </button>
          <div className="flex items-center space-x-2">
            <button onClick={() => setScale(prev => Math.max(prev - 0.2, 0.1))} className="px-2 border rounded">-</button>
            <span className="text-sm">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(prev => prev + 0.2)} className="px-2 border rounded">+</button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPageNumber(p => Math.max(p - 1, 1))}
              disabled={pageNumber <= 1}
              className="px-2 border rounded"
            >Prev</button>
            <button
              onClick={() => setPageNumber(p => Math.min(p + 1, numPages || p))}
              disabled={pageNumber >= (numPages || 1)}
              className="px-2 border rounded"
            >Next</button>
          </div>
        </div>
        <div ref={containerRef} className="flex-1 overflow-auto bg-gray-100">
          {previewUrl && (
            <Document file={previewUrl} onLoadSuccess={onDocumentLoad}>              
              <Page pageNumber={pageNumber} scale={scale} onLoadSuccess={onPageLoad} />
            </Document>
          )}
        </div>
      </aside>

      {/* Inline Edit Table */}
      <main className="w-1/2 overflow-auto p-4">
        <h2 className="text-xl font-bold mb-4">Inline Edit Metadata</h2>
        <table className="w-full table-auto border-collapse">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th className="border p-2">Preview</th>
              <th className="border p-2">Number</th>
              <th className="border p-2">Title</th>
              <th className="border p-2">Type</th>
              <th className="border p-2">Rev Date</th>
              <th className="border p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {pagesFlat.map(page => (
              <tr
                key={page.id}
                className={`hover:bg-gray-50 cursor-pointer ${page.id === previewPageId ? 'bg-gray-200' : ''}`}
                onClick={() => setPreviewPageId(page.id)}
              >
                <td className="border p-2 text-center">
                  {urls[page.id] ? (
                    <Document file={urls[page.id]}>
                      <Page pageNumber={1} width={80} />
                    </Document>
                  ) : (
                    <span className="text-sm text-gray-500">Loading...</span>
                  )}
                </td>
                <td className="border p-2">
                  <input
                    type="text"
                    className="w-full p-1 border rounded"
                    defaultValue={page.drawingNumber}
                    onBlur={e => handleSaveField(page.id, 'drawingNumber', e.target.value)}
                  />
                </td>
                <td className="border p-2">
                  <input
                    type="text"
                    className="w-full p-1 border rounded"
                    defaultValue={page.drawingTitle}
                    onBlur={e => handleSaveField(page.id, 'drawingTitle', e.target.value)}
                  />
                </td>
                <td className="border p-2">
                  <select
                    defaultValue={page.drawingType}
                    className="w-full p-1 border rounded"
                    onChange={e => handleSaveField(page.id, 'drawingType', e.target.value)}
                  >
                    {['Architectural','Structural Steel','Mechanical','Electrical','Civil','Landscape','Miscellaneous'].map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </td>
                <td className="border p-2">
                  <input
                    type="date"
                    className="w-full p-1 border rounded"
                    defaultValue={page.drawingRevDate}
                    onBlur={e => handleSaveField(page.id, 'drawingRevDate', e.target.value)}
                  />
                </td>
                <td className="border p-2 text-center">{page.needsReview ? '⚠️' : '✅'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </div>
  );
}